import { Box, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import Ticker from '../../ui/ticker';
import ScoreDisplayTicker from '../../ui/ticker/scoreDisplayTicker';
import { GameStatus } from '../../../constants/gameConstants';
import { liveGameDocs } from '../../../helpers/games';
import { useColorMode } from '../../../hooks/colorModeProvider';

const RECENT_WINDOW_MS = 30 * 60 * 1000;

const getUpdatedAtMs = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

// Create scroller for active games
const LiveTicker = () => {
  const eventId = useParams().eventId;
  const { mode, setMode } = useColorMode();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);

  useEffect(() => {
    setMode('dark');
  }, [mode, setMode]);

  useEffect(() => {
    setLoading(true);
    setGames([]);
    const collectionRef = collection(store, 'games');
    const q = query(collectionRef, where('event', '==', eventId));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const now = Date.now();

        // liveGameDocs covers the deletedAt marker as well as the older
        // `deleted` flag this used to test on its own.
        const gamesData = liveGameDocs(querySnapshot)
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .filter((game) => {
            const isActive = [GameStatus.IN_PROGRESS, GameStatus.PLAYOFF].includes(game.status);
            const isRecentlyUpdated = now - getUpdatedAtMs(game.updatedAt) <= RECENT_WINDOW_MS;

            return game.live || isActive || isRecentlyUpdated;
          })
          .sort((a, b) => {
            if (Boolean(a.live) !== Boolean(b.live)) {
              return a.live ? -1 : 1;
            }

            const activeA = [GameStatus.IN_PROGRESS, GameStatus.PLAYOFF].includes(a.status);
            const activeB = [GameStatus.IN_PROGRESS, GameStatus.PLAYOFF].includes(b.status);
            if (activeA !== activeB) {
              return activeA ? -1 : 1;
            }

            const updatedDiff = getUpdatedAtMs(b.updatedAt) - getUpdatedAtMs(a.updatedAt);
            if (updatedDiff !== 0) {
              return updatedDiff;
            }

            return `${a.green || ''}${a.rink || 0}`.localeCompare(
              `${b.green || ''}${b.rink || 0}`,
              undefined,
              {
                numeric: true
              }
            );
          });

        setGames(gamesData);
        setLoading(false);
      },
      () => {
        setGames([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  return (
    <Box m={2}>
      {loading ? (
        <Typography color="text.secondary">Loading ticker...</Typography>
      ) : games.length === 0 ? (
        <Typography color="text.secondary">No live or recently updated games.</Typography>
      ) : (
        <Ticker>
          {games.map((game) => (
            <Paper
              key={game.id}
              sx={{
                minWidth: 320,
                px: 2,
                py: 1,
                borderRadius: 5,
                border: '2px solid',
                borderColor: game.status === GameStatus.PLAYOFF ? 'warning.main' : 'info.main'
              }}
              elevation={3}>
              <ScoreDisplayTicker game={game} />
            </Paper>
          ))}
        </Ticker>
      )}
    </Box>
  );
};

export default LiveTicker;
